import * as React from 'react';
import { cn } from '../../../utils';
import type { ZineAgendaItem } from './agenda-band';
import { zineZeroPad } from './zine-theme';

export interface BlockNavPillLabels {
  /** accessible name for the pill <nav>, e.g. "Class sections" */
  nav: string;
  /** previous-block button, e.g. "Previous section" */
  prev: string;
  /** next-block button, e.g. "Next section" */
  next: string;
  /** "03 of 08" counter shown in the pill */
  counter: string;
  /** accessible hint on the pill's middle button, e.g. "Show all sections" */
  open: string;
  /** sheet heading, e.g. "In this class" */
  sheetTitle: string;
  /** close button, e.g. "Close" */
  close: string;
  /** tag on the current row, e.g. "Now" */
  now: string;
}

export interface BlockNavPillProps {
  /** the same items the AgendaBand renders (title / kind / minutes / done) */
  items: ZineAgendaItem[];
  /** 0-based index of the block currently in the reading band */
  currentIndex: number;
  /** jump to a block (0-based) — the parent owns the scrolling */
  onJump: (index: number) => void;
  /**
   * When true the pill slides out of view (parent hides it while the
   * top-of-issue agenda is on screen). The sheet is never affected.
   */
  hidden?: boolean;
  labels: BlockNavPillLabels;
  className?: string;
}

/**
 * Walks up from `el` to the nearest scrollable ancestor (the p2e shell
 * scrolls a content <div>, not the window) so the phone hide-on-scroll can
 * listen on the element that actually scrolls.
 */
function findScrollParent(el: HTMLElement | null): HTMLElement | Window {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return window;
}

/** Number of pixels of downward scroll before the pill ducks away. */
const HIDE_DELTA = 8;
/** Idle time (ms) after which the pill comes back on its own. */
const REVEAL_AFTER_IDLE_MS = 900;
/** Matches the zine's single-column breakpoint (`.z-cols` collapse). */
const PHONE_QUERY = '(max-width: 720px)';

/**
 * Folio pill: a small dark "03 of 08 · Title" capsule pinned at the foot of
 * the class issue, with previous / next arrows. Tapping the middle lifts the
 * class agenda as a bottom sheet so students can jump to any block without
 * scrolling back to the top of the issue. Sits centred under the reading
 * column (bottom-left on phones) — the bottom-right corner belongs to the AI
 * chat launcher. On phones it ducks while scrolling down and returns on
 * scroll-up or after a short pause.
 */
export function BlockNavPill({
  items,
  currentIndex,
  onJump,
  hidden = false,
  labels,
  className,
}: BlockNavPillProps) {
  const [open, setOpen] = React.useState(false);
  const [ducked, setDucked] = React.useState(false);
  const navRef = React.useRef<HTMLElement>(null);
  const midRef = React.useRef<HTMLButtonElement>(null);
  const closeRef = React.useRef<HTMLButtonElement>(null);

  const total = items.length;
  const safeIndex = Math.min(Math.max(currentIndex, 0), Math.max(total - 1, 0));
  const current = items[safeIndex];

  // Phone-only hide on scroll-down / show on scroll-up.
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(PHONE_QUERY);
    const scroller = findScrollParent(navRef.current);
    let lastTop = scroller instanceof Window ? scroller.scrollY : scroller.scrollTop;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;

    const onScroll = () => {
      if (!media.matches) {
        setDucked(false);
        return;
      }
      const top = scroller instanceof Window ? scroller.scrollY : scroller.scrollTop;
      const delta = top - lastTop;
      lastTop = top;
      if (delta > HIDE_DELTA) {
        setDucked(true);
      } else if (delta < -HIDE_DELTA / 2) {
        setDucked(false);
      }
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => setDucked(false), REVEAL_AFTER_IDLE_MS);
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(idleTimer);
      scroller.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Centre the pill under the reading column rather than the viewport: on
  // desktop the app sidebar shifts the column right of the viewport centre.
  React.useEffect(() => {
    const nav = navRef.current;
    const column = nav?.parentElement;
    if (!nav || !column || typeof ResizeObserver === 'undefined') {
      return;
    }
    const place = () => {
      const rect = column.getBoundingClientRect();
      nav.style.setProperty('--z-folio-x', `${Math.round(rect.left + rect.width / 2)}px`);
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(column);
    window.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
    };
  }, []);

  // Sheet: Escape closes; focus moves in on open and back to the pill on close.
  React.useEffect(() => {
    if (!open) {
      return;
    }
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const close = React.useCallback(() => {
    setOpen(false);
    midRef.current?.focus();
  }, []);

  const jump = React.useCallback(
    (index: number) => {
      if (index < 0 || index >= total) {
        return;
      }
      onJump(index);
    },
    [onJump, total],
  );

  if (total === 0 || !current) {
    return null;
  }

  const progressPct = Math.round(((safeIndex + 1) / total) * 100);

  return (
    <>
      <nav
        ref={navRef}
        className={cn('z-folio', (hidden || ducked) && 'z-folio--hidden', className)}
        aria-label={labels.nav}
        data-testid="block-nav-pill"
      >
        <button
          type="button"
          className="z-folio-arrow"
          aria-label={labels.prev}
          disabled={safeIndex <= 0}
          onClick={() => jump(safeIndex - 1)}
        >
          <svg width="10" height="16" viewBox="0 0 10 16" aria-hidden="true">
            <path d="M8.5 1.5 2 8l6.5 6.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button
          ref={midRef}
          type="button"
          className="z-folio-mid"
          aria-haspopup="dialog"
          aria-expanded={open}
          title={labels.open}
          onClick={() => setOpen(true)}
        >
          <em>{labels.counter}</em>
          <b>{current.title}</b>
        </button>
        <button
          type="button"
          className="z-folio-arrow"
          aria-label={labels.next}
          disabled={safeIndex >= total - 1}
          onClick={() => jump(safeIndex + 1)}
        >
          <svg width="10" height="16" viewBox="0 0 10 16" aria-hidden="true">
            <path d="m1.5 1.5 6.5 6.5-6.5 6.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="z-folio-prog" aria-hidden="true">
          <i style={{ width: `${progressPct}%` }} />
        </span>
      </nav>

      {open && (
        <div className="z-folio-sheet" data-testid="block-nav-sheet">
          <div className="z-folio-dim" onClick={close} aria-hidden="true" />
          <div
            className="z-folio-panel"
            role="dialog"
            aria-modal="true"
            aria-label={labels.sheetTitle}
          >
            <div className="z-folio-grab" aria-hidden="true" />
            <div className="z-folio-head">
              <p className="z-band-label">{labels.sheetTitle}</p>
              <button
                ref={closeRef}
                type="button"
                className="z-folio-close"
                aria-label={labels.close}
                onClick={close}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  <path d="M2 2l10 10M12 2 2 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <ol className="z-folio-list">
              {items.map((item, index) => {
                const isCurrent = index === safeIndex;
                return (
                  <li key={index}>
                    <button
                      type="button"
                      className={cn('z-folio-row', isCurrent && 'z-folio-row--now')}
                      aria-current={isCurrent ? 'true' : undefined}
                      onClick={() => {
                        setOpen(false);
                        jump(index);
                      }}
                    >
                      <span className="z-folio-no">{zineZeroPad(item.no)}</span>
                      <span className="z-folio-text">
                        <span className="z-folio-title">{item.title}</span>
                        {(item.kind != null || item.minutes != null) && (
                          <small>
                            {item.kind}
                            {item.kind != null && item.minutes != null && ' · '}
                            {item.minutes}
                          </small>
                        )}
                      </span>
                      <span className="z-folio-cell">
                        {isCurrent ? (
                          <span className="z-folio-now">{labels.now}</span>
                        ) : item.done ? (
                          <span className="z-folio-done">{item.doneLabel ?? '✓ done'}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
